import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams, Link } from 'react-router-dom';
import SeoHead from './SeoHead';
import AdBanner from './AdBanner';
import AdSenseSlot from './AdSenseSlot';
import CommentSection from './CommentSection';
import { Alert, Breadcrumbs, Button, Modal, Select } from './ComponentLibrary';
import { pageMeta } from '../seoUtils';
import { API_BASE } from '../config';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { BLOG_CATEGORIES, blogCategoryLabel, blogCategoryDescription } from '../config/blogCategories';
import HelpLink from './HelpLink';
import {
  ArrowRightIcon,
  HomeIcon,
  CheckCircleIcon,
  AlertCircleIcon,
  WhatsAppIcon,
  FacebookIcon,
  XIcon,
  LinkedInIcon,
  MailIcon,
} from './IconSet';

const GUIDE_PAGE_SIZE = 8;

const GUIDE_REPORT_REASONS = [
  { value: 'spam', label: 'Spam' },
  { value: 'inappropriate', label: 'Inappropriate content' },
  { value: 'plagiarism', label: 'Plagiarism / not original' },
  { value: 'misleading', label: 'Misleading information' },
  { value: 'other', label: 'Other' },
];

// Same channel set + icons as PropertyDetailsPage's share menu
// (WhatsAppIcon/FacebookIcon/XIcon/LinkedInIcon/MailIcon from IconSet.js).
const SHARE_LINKS = (url, title) => [
  { key: 'whatsapp', label: 'WhatsApp', Icon: WhatsAppIcon, href: `https://wa.me/?text=${encodeURIComponent(`${title} ${url}`)}` },
  { key: 'x', label: 'X', Icon: XIcon, href: `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}` },
  { key: 'facebook', label: 'Facebook', Icon: FacebookIcon, href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}` },
  { key: 'linkedin', label: 'LinkedIn', Icon: LinkedInIcon, href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}` },
  { key: 'email', label: 'Email', Icon: MailIcon, href: `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(url)}` },
];

/* ─── Blog index component ────────────────────────────────────────── */

function BlogIndexPage() {
  const navigate = useNavigate();
  const { user, authedFetch, hasRole } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCategory = searchParams.get('category') || '';
  const [posts, setPosts] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  // Whether the logged-in visitor is already an approved contributor - the
  // hero CTA below routes straight to the editor for them instead of
  // "Become a Contributor" (confusing wording for someone already approved,
  // and previously the *only* way to reach /blog/write was to click that
  // button and get redirected through /blog/submit).
  const [isApprovedContributor, setIsApprovedContributor] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!user) {
      setIsApprovedContributor(false);
      return undefined;
    }
    authedFetch(`${API_BASE}/contributor-applications/mine`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!cancelled) {
          setIsApprovedContributor(data?.profile?.status === 'approved');
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [user, authedFetch]);

  // Deliberately distinct from marketplace-page metadata (search/browse) -
  // this page is the property *knowledge* side of NaijaNest, not a listings
  // search, so its title/description talk about insights and advice, not
  // "find a property." A category filter gets its own title/description
  // (not the generic Guide-wide one), so /blog?category=renting reads as a
  // real topical page to search engines, not a query-string variant of /blog.
  const categoryLabel = activeCategory ? blogCategoryLabel(activeCategory) : '';
  const seo = pageMeta({
    title: activeCategory
      ? `${categoryLabel} - Property Guide`
      : 'Property Guide - Insights, Advice & Market Knowledge',
    description: activeCategory
      ? blogCategoryDescription(activeCategory)
      : 'Property insights, advice and stories from across Nigeria: renting, buying, investment, land documentation, neighbourhood guides and market trends, from NaijaNest and community contributors.',
    path: activeCategory ? `/blog?category=${activeCategory}` : '/blog',
  });

  // Re-fetches from page one whenever the category filter changes -
  // "Load more" below only ever appends the *next* page onto whatever
  // this effect last set, never re-runs it itself.
  useEffect(() => {
    let cancelled = false;
    const categoryQs = activeCategory ? `&category=${encodeURIComponent(activeCategory)}` : '';
    fetch(`${API_BASE}/blog-posts?limit=${GUIDE_PAGE_SIZE}&offset=0${categoryQs}`)
      .then((res) => (res.ok ? res.json() : { posts: [], total: 0 }))
      .then((data) => {
        if (!cancelled) {
          setPosts(data.posts || []);
          setTotalCount(data.total || 0);
        }
      })
      .catch(() => {
        // Non-fatal - the page just shows nothing until the next load.
      });
    return () => {
      cancelled = true;
    };
  }, [activeCategory]);

  const handleLoadMore = () => {
    if (loadingMore) {return;}
    setLoadingMore(true);
    const categoryQs = activeCategory ? `&category=${encodeURIComponent(activeCategory)}` : '';
    fetch(`${API_BASE}/blog-posts?limit=${GUIDE_PAGE_SIZE}&offset=${posts.length}${categoryQs}`)
      .then((res) => (res.ok ? res.json() : { posts: [], total: totalCount }))
      .then((data) => {
        setPosts((prev) => [...prev, ...(data.posts || [])]);
        setTotalCount(data.total ?? totalCount);
      })
      .catch(() => {
        // Non-fatal - the button just stays available to retry.
      })
      .finally(() => setLoadingMore(false));
  };

  // Curated (editorial) guides and community-submitted guides are both
  // rows in the same table now - the server already orders them (curated
  // leads only while there's no real community content yet, and demotes
  // once there is; an admin-pinned guide always leads regardless of
  // source), so the client just renders them in the order it receives.
  const cards = posts.map((post) => ({
    slug: post.slug,
    title: post.title,
    description: post.excerpt || (post.source === 'curated' ? post.body : `By ${post.author_name || 'a NaijaNest member'}`),
    hero: post.cover_image_url,
    date: post.approved_at || post.created_at,
    category: post.category,
    source: post.source,
    authorName: post.author_name,
    upvotes: post.upvotes || 0,
    downvotes: post.downvotes || 0,
  }));

  return (
    <div className="static-page blog-page">
      <SeoHead title={seo.fullTitle} description={seo.desc} url={seo.url} image={seo.img} />
      <Breadcrumbs items={[{ label: 'Home', href: '/' }, { label: 'Property Guide', current: true }]} />
      <section className="section blog-index">
        <header className="blog-page__intro">
          <h1>NaijaNest Property Guide</h1>
          <p className="static-page__lead">
            Property insights, advice and stories from across Nigeria, written by our editorial
            team and vetted contributors.
          </p>
          <div className="blog-page__intro-actions">
            {isApprovedContributor || hasRole('admin') ? (
              <>
                <Button onClick={() => navigate('/blog/dashboard')}>Dashboard</Button>
                <Button variant="secondary" onClick={() => navigate('/blog/write')}>
                  Write an Article
                </Button>
              </>
            ) : (
              <Button onClick={() => navigate('/blog/submit')}>Become a Contributor</Button>
            )}
          </div>
          <HelpLink to="/help" className="blog-page__help-link">
            Looking for help instead of a guide? Visit the Help Centre
          </HelpLink>
        </header>

        <AdBanner slot="top" />

        <nav className="blog-cat-row" aria-label="Filter by category" id="blog-articles">
          <button
            type="button"
            className={`blog-cat-chip${!activeCategory ? ' blog-cat-chip--active' : ''}`}
            onClick={() => setSearchParams({})}
          >
            All
          </button>
          {BLOG_CATEGORIES.map((cat) => (
            <button
              key={cat.slug}
              type="button"
              className={`blog-cat-chip${activeCategory === cat.slug ? ' blog-cat-chip--active' : ''}`}
              onClick={() => setSearchParams({ category: cat.slug })}
            >
              {cat.label}
            </button>
          ))}
        </nav>

        {activeCategory && (
          <div className="blog-category-header">
            <h2>{categoryLabel}</h2>
            <p>{blogCategoryDescription(activeCategory)}</p>
          </div>
        )}

        {cards.length === 0 ? (
          <p className="admin-muted">No articles in this category yet.</p>
        ) : (
          <>
            <div className="blog-toolbar">
              <span className="blog-toolbar__count">
                Showing {cards.length} of {totalCount} article{totalCount === 1 ? '' : 's'}
              </span>
              <div className="blog-view-toggle" role="group" aria-label="Article view">
                <button
                  type="button"
                  className={`blog-view-toggle__btn${viewMode === 'grid' ? ' blog-view-toggle__btn--active' : ''}`}
                  onClick={() => setViewMode('grid')}
                  aria-pressed={viewMode === 'grid'}
                >
                  ▦ Cards
                </button>
                <button
                  type="button"
                  className={`blog-view-toggle__btn${viewMode === 'list' ? ' blog-view-toggle__btn--active' : ''}`}
                  onClick={() => setViewMode('list')}
                  aria-pressed={viewMode === 'list'}
                >
                  ☰ List
                </button>
              </div>
            </div>

            <div className={viewMode === 'grid' ? 'blog-grid' : 'blog-list'}>
              {cards.map((card, index) => {
                const isLead = viewMode === 'grid' && index === 0 && !activeCategory;
                return (
                  <article
                    key={card.slug}
                    className={
                      viewMode === 'grid'
                        ? `blog-card${isLead ? ' blog-card--lead' : ''}`
                        : 'blog-row'
                    }
                    onClick={() => navigate(`/blog/${card.slug}`)}
                    role="link"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && navigate(`/blog/${card.slug}`)}
                  >
                    {card.hero && (
                      <img
                        className={viewMode === 'grid' ? 'blog-card__hero' : 'blog-row__hero'}
                        src={card.hero}
                        alt={card.title}
                        loading="lazy"
                      />
                    )}
                    <div className={viewMode === 'grid' ? 'blog-card__body' : 'blog-row__body'}>
                      <div className="blog-card__meta">
                        <span className={`blog-author-type blog-author-type--${card.source === 'curated' ? 'editorial' : 'contributor'}`}>
                          {card.source === 'curated' ? 'Editorial' : 'Contributor'}
                        </span>
                        {card.category && <span className="blog-tag">{blogCategoryLabel(card.category)}</span>}
                      </div>
                      <h2 className={viewMode === 'grid' ? 'blog-card__title' : 'blog-row__title'}>
                        {card.title}
                      </h2>
                      <p className="blog-card__desc">{card.description}</p>
                      <span className="blog-card__date">
                        {new Date(card.date).toLocaleDateString('en-NG', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        })}
                      </span>
                      <span className="blog-card__reactions">
                        👍 {card.upvotes} · 👎 {card.downvotes}
                      </span>
                    </div>
                  </article>
                );
              })}
            </div>

            {cards.length < totalCount && (
              <div className="blog-load-more">
                <Button variant="secondary" onClick={handleLoadMore} disabled={loadingMore}>
                  {loadingMore ? 'Loading…' : 'Load more articles'}
                </Button>
              </div>
            )}
          </>
        )}

        <AdSenseSlot slotId={process.env.REACT_APP_ADSENSE_BLOG_SLOT} className="blog-page__adsense" />
      </section>
    </div>
  );
}

/* ─── Single article component ────────────────────────────────────── */

function BlogArticlePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user, authedFetch } = useAuth();
  const { showToast } = useToast();

  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reactionLoading, setReactionLoading] = useState(false);
  const [related, setRelated] = useState([]);

  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [reportReason, setReportReason] = useState('spam');
  const [reportDetails, setReportDetails] = useState('');
  const [reportStatus, setReportStatus] = useState('');
  const [reportLoading, setReportLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    // authFetch (not a bare fetch) so a logged-in viewer's own reaction
    // comes back with the post - the endpoint uses optionalAuth, so an
    // anonymous visitor still gets the post back either way.
    authedFetch(`${API_BASE}/blog-posts/${encodeURIComponent(slug)}`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!cancelled) {
          setPost(data?.post || null);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setPost(null);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  // Related Articles: same category as this one, not just "newest" - a
  // meaningful relationship rather than a recency list.
  useEffect(() => {
    if (!post?.category) {
      setRelated([]);
      return undefined;
    }
    let cancelled = false;
    fetch(`${API_BASE}/blog-posts?category=${encodeURIComponent(post.category)}`)
      .then((res) => (res.ok ? res.json() : { posts: [] }))
      .then((data) => {
        if (!cancelled) {
          setRelated((data.posts || []).filter((p) => p.slug !== post.slug).slice(0, 3));
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [post?.category, post?.slug]);

  const handleReact = async (reaction) => {
    if (!user) {
      navigate('/login', { state: { from: `/blog/${slug}` } });
      return;
    }
    if (reactionLoading || !post) {return;}
    setReactionLoading(true);
    try {
      const response = await authedFetch(`${API_BASE}/blog-posts/${post.id}/react`, {
        method: 'POST',
        body: JSON.stringify({ reaction }),
      });
      if (!response.ok) {
        throw new Error('Failed to save reaction');
      }
      const data = await response.json();
      setPost((prev) => (prev ? { ...prev, ...data } : prev));
    } catch (err) {
      // Reactions are a minor, non-blocking affordance - fail silently
      // rather than interrupting the reading experience with an error banner.
    } finally {
      setReactionLoading(false);
    }
  };

  const handleReport = async (event) => {
    event.preventDefault();
    if (!post) {return;}
    setReportLoading(true);
    setReportStatus('');
    try {
      const response = await authedFetch(`${API_BASE}/blog-posts/${post.id}/report`, {
        method: 'POST',
        body: JSON.stringify({ reason: reportReason, description: reportDetails.trim() }),
      });
      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.error || 'Failed to submit report.');
      }
      setReportStatus('success');
      setReportDetails('');
    } catch (err) {
      showToast(err.message || 'Failed to submit report.', 'error');
    } finally {
      setReportLoading(false);
    }
  };

  const seo = useMemo(() => {
    if (post) {
      // The article's own SEO description (or excerpt) takes priority over
      // a generic body slice - per-article metadata should reflect what
      // the article is actually about, not a truncated first sentence.
      return pageMeta({
        title: post.seo_title || post.title,
        description: post.seo_description || post.excerpt || post.body.replace(/<[^>]*>/g, '').slice(0, 160),
        path: `/blog/${slug}`,
        image: post.cover_image_url,
      });
    }
    return pageMeta({ title: 'Article Not Found', path: `/blog/${slug}`, noIndex: true });
  }, [post, slug]);

  const jsonLd = useMemo(() => {
    if (!post) {
      return null;
    }
    const isEditorial = post.source === 'curated';
    return [
      {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: post.title,
        description: post.seo_description || post.excerpt || post.body.replace(/<[^>]*>/g, '').slice(0, 160),
        image: post.cover_image_url,
        datePublished: post.approved_at || post.created_at,
        dateModified: post.updated_at || post.approved_at || post.created_at,
        author: isEditorial
          ? { '@type': 'Organization', name: 'Naija Nest Properties' }
          : {
            '@type': 'Person',
            name: post.author_name || 'NaijaNest contributor',
            ...(post.author_role ? { jobTitle: post.author_role } : {}),
            url: `https://naijanestproperties.com/seller/${post.author_id}`,
          },
        publisher: {
          '@type': 'Organization',
          name: 'Naija Nest Properties',
          url: 'https://naijanestproperties.com',
        },
        mainEntityOfPage: `https://naijanestproperties.com/blog/${post.slug}`,
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://naijanestproperties.com/' },
          { '@type': 'ListItem', position: 2, name: 'Property Guide', item: 'https://naijanestproperties.com/blog' },
          { '@type': 'ListItem', position: 3, name: post.title, item: `https://naijanestproperties.com/blog/${post.slug}` },
        ],
      },
    ];
  }, [post]);

  if (loading) {
    return (
      <div className="static-page blog-page">
        <SeoHead title={seo.fullTitle} description={seo.desc} url={seo.url} image={seo.img} noIndex />
        <section className="section">
          <p className="admin-muted">Loading…</p>
        </section>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="static-page blog-page">
        <SeoHead
          title={seo.fullTitle}
          description={seo.desc}
          url={seo.url}
          image={seo.img}
          noIndex
        />
        <Breadcrumbs
          items={[
            { label: 'Home', href: '/' },
            { label: 'Property Guide', href: '/blog' },
            { label: 'Not found', current: true },
          ]}
        />
        <section className="section">
          <h1>Article not found</h1>
          <p>The article you're looking for doesn't exist.</p>
          <button type="button" className="btn btn-primary" onClick={() => navigate('/blog')}>
            Back to Property Guide
          </button>
        </section>
      </div>
    );
  }

  const hasSections = Array.isArray(post.sections) && post.sections.length > 0;
  const isEditorial = post.source === 'curated';
  const shareUrl = `https://naijanestproperties.com/blog/${post.slug}`;

  return (
    <div className="static-page blog-page">
      <SeoHead
        title={seo.fullTitle}
        description={seo.desc}
        url={seo.url}
        image={seo.img}
        jsonLd={jsonLd}
      />
      <Breadcrumbs
        items={[
          { label: 'Home', href: '/' },
          { label: 'Property Guide', href: '/blog' },
          { label: post.title, current: true },
        ]}
      />

      <div className="section blog-article-grid">
        <article className="blog-article blog-article-main-col">
          {post.cover_image_url && (
            <img className="blog-article__hero" src={post.cover_image_url} alt={post.title} />
          )}

          <span className={`blog-author-type blog-author-type--${isEditorial ? 'editorial' : 'contributor'}`}>
            {isEditorial ? 'Editorial' : 'Contributor Article'}
          </span>
          <h1>{post.title}</h1>
          {(post.category || (post.tags || []).length > 0) && (
            <div className="blog-article__taxonomy">
              {post.category && (
                <button type="button" className="blog-tag blog-tag--link" onClick={() => navigate(`/blog?category=${post.category}`)}>
                  {blogCategoryLabel(post.category)}
                </button>
              )}
              {(post.tags || []).map((tag) => (
                <span key={tag} className="blog-tag">#{tag}</span>
              ))}
            </div>
          )}


          <p className="blog-article__date">
            {isEditorial ? `By ${post.author_name || 'NaijaNest Editorial'} · ` : ''}
            {new Date(post.approved_at || post.created_at).toLocaleDateString('en-NG', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })}
            {post.updated_at && post.updated_at !== post.created_at && (
              <> · Updated {new Date(post.updated_at).toLocaleDateString('en-NG', { year: 'numeric', month: 'long', day: 'numeric' })}</>
            )}
          </p>

          {hasSections ? (
            <>
              <p className="blog-article__lead">{post.body}</p>
              <div className="blog-article__news-grid" aria-label="Article sections">
                {post.sections.map((sec, i) => (
                  <section
                    key={i}
                    className={`blog-article__section${i === 0 ? ' blog-article__section--lead' : ''}`}
                  >
                    <h2>
                      <CheckCircleIcon size={18} /> {sec.heading}
                    </h2>
                    <p>{sec.body}</p>
                    {sec.link && (
                      <a
                        href={sec.link}
                        className="blog-article__cta"
                        onClick={(e) => {
                          e.preventDefault();
                          navigate(sec.link);
                        }}
                      >
                        Browse properties <ArrowRightIcon size={14} />
                      </a>
                    )}
                  </section>
                ))}
              </div>
            </>
          ) : post.content_format === 'html' ? (
          // Sanitized server-side (sanitize-html allowlist, see
          // server/blog-posts-api.js) on every save, not just at publish
          // time - safe to render as-is.
            <div className="blog-article__community-body" dangerouslySetInnerHTML={{ __html: post.body }} />
          ) : (
            <div className="blog-article__community-body">
              {post.body.split(/\n{2,}/).map((paragraph, i) => (
                <p key={i}>{paragraph}</p>
              ))}
            </div>
          )}

          {post.linkedProperties?.length > 0 && (
            <div className="blog-article__related-props">
              <h3>Related listings</h3>
              <div className="blog-article__prop-row">
                {post.linkedProperties.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    className="blog-article__prop-card"
                    onClick={() => navigate(`/property/${p.id}`)}
                  >
                    {p.images?.[0] && <img src={p.images[0]} alt={p.title} />}
                    <span className="blog-article__prop-price">₦{Number(p.price || 0).toLocaleString()}</span>
                    <span className="blog-article__prop-loc">{p.title}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="blog-article__reactions">
            <button
              type="button"
              className={`blog-article__reaction-btn${
                post.userReaction === 'up' ? ' blog-article__reaction-btn--active' : ''
              }`}
              disabled={reactionLoading}
              onClick={() => handleReact('up')}
              aria-pressed={post.userReaction === 'up'}
            >
              👍 <span>{post.upvotes || 0}</span>
            </button>
            <button
              type="button"
              className={`blog-article__reaction-btn${
                post.userReaction === 'down' ? ' blog-article__reaction-btn--active' : ''
              }`}
              disabled={reactionLoading}
              onClick={() => handleReact('down')}
              aria-pressed={post.userReaction === 'down'}
            >
              👎 <span>{post.downvotes || 0}</span>
            </button>
            <button
              type="button"
              className="blog-article__reaction-btn blog-article__reaction-btn--report"
              onClick={() => {
                setReportStatus('');
                setIsReportModalOpen(true);
              }}
            >
              <AlertCircleIcon size={14} /> <span>Report</span>
            </button>
          </div>

          <div className="blog-article__share">
            <span className="blog-article__share-label">Share:</span>
            {SHARE_LINKS(shareUrl, post.title).map(({ key, label, Icon, href }) => (
              <a
                key={key}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="blog-article__share-btn"
                aria-label={`Share on ${label}`}
                title={label}
              >
                <Icon size={16} />
              </a>
            ))}
          </div>

          {related.length > 0 && (
            <div className="blog-article__related-articles">
              <h3>Related Articles</h3>
              <div className="blog-grid">
                {related.map((r) => (
                  <article
                    key={r.slug}
                    className="blog-card"
                    onClick={() => navigate(`/blog/${r.slug}`)}
                    role="link"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && navigate(`/blog/${r.slug}`)}
                  >
                    {r.cover_image_url && <img className="blog-card__hero" src={r.cover_image_url} alt={r.title} loading="lazy" />}
                    <div className="blog-card__body">
                      <h3 className="blog-card__title">{r.title}</h3>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          )}

          <CommentSection postId={post.id} />
        </article>

        {/* Same side-column pattern as .property-detail-side-col on
          PropertyDetailsPage: a "who this is from" card up top (there,
          "Listed by"; here, "Contributed by"), then ads - same sticky
          behaviour so both stay in view while the reader scrolls. */}
        <aside className="blog-article-side-col">
          <div className="blog-article__contributor-card">
            <p className="blog-article__contributor-card-label">
              {isEditorial ? 'Published by' : 'Contributed by'}
            </p>
            {isEditorial ? (
              <div className="blog-article__byline">
                <span className="blog-article__byline-avatar blog-article__byline-avatar--fallback">N</span>
                <span>
                  <span className="blog-article__byline-name">NaijaNest Editorial</span>
                  <span className="blog-article__byline-role">Property Guide team</span>
                </span>
              </div>
            ) : (
              <button type="button" className="blog-article__byline" onClick={() => navigate(`/seller/${post.author_id}`)}>
                {post.author_avatar_url ? (
                  <img className="blog-article__byline-avatar" src={post.author_avatar_url} alt="" />
                ) : (
                  <span className="blog-article__byline-avatar blog-article__byline-avatar--fallback">
                    {(post.author_name || '?')[0]}
                  </span>
                )}
                <span>
                  <span className="blog-article__byline-name">{post.author_name || 'a NaijaNest member'}</span>
                  {post.author_role && (
                    <span className="blog-article__byline-role">
                      {post.author_role}
                      {post.author_organisation ? ` · ${post.author_organisation}` : ''}
                    </span>
                  )}
                </span>
              </button>
            )}
          </div>

          <AdBanner slot="sidebar" smoothScroll={false} />
          <AdSenseSlot
            key={slug}
            slotId={process.env.REACT_APP_ADSENSE_BLOG_SLOT}
            className="blog-page__adsense"
          />

          <div className="card property-related-searches-card">
            <p className="section-note">Explore more guides</p>
            <ul className="property-related-searches-card__list">
              {BLOG_CATEGORIES.filter((c) => c.slug !== post.category)
                .slice(0, 4)
                .map((c) => (
                  <li key={c.slug}>
                    <Link to={`/blog?category=${c.slug}`}>{c.label}</Link>
                  </li>
                ))}
            </ul>
            <Link to="/blog" className="property-related-searches-card__all">
              Browse all guides &rarr;
            </Link>
          </div>

          <Link to="/browse" className="card property-advertise-card blog-article__browse-props-card">
            <span className="property-advertise-card__icon" aria-hidden="true">
              <HomeIcon size={22} />
            </span>
            <p className="property-advertise-card__title">Looking for a place?</p>
            <p className="property-advertise-card__subtitle">
              Browse thousands of verified listings across Nigeria.
            </p>
            <span className="property-advertise-card__cta">Browse properties &rarr;</span>
          </Link>

          <Link to="/list-property" className="card property-advertise-card">
            <span className="property-advertise-card__icon" aria-hidden="true">
              📢
            </span>
            <p className="property-advertise-card__title">Got a property to sell or rent?</p>
            <p className="property-advertise-card__subtitle">
              Reach thousands of buyers and tenants on NaijaNest.
            </p>
            <span className="property-advertise-card__cta">Get started &rarr;</span>
          </Link>

          <Link to="/help" className="card property-advertise-card">
            <span className="property-advertise-card__icon" aria-hidden="true">
              🆘
            </span>
            <p className="property-advertise-card__title">Need help with something else?</p>
            <p className="property-advertise-card__subtitle">
              Visit our Help Centre for answers on listing, messaging, safety and more.
            </p>
            <span className="property-advertise-card__cta">Visit Help Centre &rarr;</span>
          </Link>
        </aside>
      </div>

      <Modal
        isOpen={isReportModalOpen}
        onClose={() => setIsReportModalOpen(false)}
        title="Report this guide"
      >
        <HelpLink to="/help?category=safety" className="property-report-help-link">
          Safety &amp; reporting guidance
        </HelpLink>
        {reportStatus === 'success' ? (
          <>
            <Alert type="success">
              Thank you, your report has been submitted and will be reviewed by our team.
            </Alert>
            <div className="property-report-actions">
              <Button variant="secondary" onClick={() => setIsReportModalOpen(false)}>
                Close
              </Button>
            </div>
          </>
        ) : (
          <form onSubmit={handleReport} className="property-report-form">
            <label className="property-report-label" htmlFor="guide-report-reason">
              Reason for reporting
            </label>
            <Select
              id="guide-report-reason"
              value={reportReason}
              onChange={(e) => setReportReason(e.target.value)}
              options={GUIDE_REPORT_REASONS}
            />
            <label className="property-report-label" htmlFor="guide-report-details">
              Additional details <span className="admin-muted">(optional)</span>
            </label>
            <textarea
              id="guide-report-details"
              className="property-report-textarea"
              rows={4}
              maxLength={500}
              placeholder="Describe the issue…"
              value={reportDetails}
              onChange={(e) => setReportDetails(e.target.value)}
            />
            <div className="property-report-actions">
              <Button type="submit" variant="primary" disabled={reportLoading}>
                {reportLoading ? 'Submitting…' : 'Submit report'}
              </Button>
              <Button
                type="button"
                variant="secondary"
                disabled={reportLoading}
                onClick={() => setIsReportModalOpen(false)}
              >
                Cancel
              </Button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
}

export { BlogIndexPage, BlogArticlePage };
export default BlogIndexPage;
